import React, { useState, useEffect, useRef } from 'react';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, setDoc,
  doc, onSnapshot, orderBy, serverTimestamp, getDoc, limit, arrayUnion, arrayRemove
} from 'firebase/firestore';

const C = {
  bg:'#0B1928', bgCard:'#132030',
  border:'rgba(255,255,255,0.09)', borderMid:'rgba(255,255,255,0.14)',
  orange:'#FF6B00', orangeDim:'rgba(255,107,0,0.12)', orangeBorder:'rgba(255,107,0,0.35)',
  textPrimary:'#F0F4F8', textSecondary:'#8496A8', textMuted:'#5A7A9A',
  green:'#00C851', greenDim:'rgba(0,200,81,0.10)', greenBorder:'rgba(0,200,81,0.28)',
  blue:'#3B82F6', blueDim:'rgba(59,130,246,0.12)',
};
const font = `'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;

function getLocalDateString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function friendshipId(uid1, uid2) {
  return [uid1, uid2].sort().join('_');
}

function Avatar({ username, size = 36, orange = false }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      backgroundColor: orange ? C.orangeDim : C.bgCard,
      border: `1px solid ${orange ? C.orangeBorder : C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.38), fontWeight: '700',
      color: orange ? C.orange : C.textSecondary, flexShrink: 0,
      fontFamily: font,
    }}>
      {(username || '?').charAt(0).toUpperCase()}
    </div>
  );
}

// ── INVITE FRIENDS MODAL ──────────────────────────────────────────────────────
function InviteFriendsModal({ user, myUsername, onClose }) {
  const [email,      setEmail]      = useState('');
  const [message,    setMessage]    = useState('');
  const [sending,    setSending]    = useState(false);
  const [sent,       setSent]       = useState(false);
  const [error,      setError]      = useState('');

  const handleSend = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) { setError('Please enter an email address'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) { setError('Please enter a valid email address'); return; }
    setError('');
    setSending(true);
    try {
      const sendInvite = httpsCallable(functions, 'sendInviteEmail');
      await sendInvite({
        toEmail: trimmed,
        fromUserId: user.uid,
        fromUsername: myUsername,
        personalMessage: message.trim(),
      });
      setSent(true);
    } catch(e) {
      console.error(e);
      if (e.message?.includes('already invited')) {
        setError('You already invited this person recently.');
      } else {
        setError('Failed to send invite. Try again.');
      }
    }
    setSending(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.8)', zIndex:200, display:'flex', alignItems:'flex-end', justifyContent:'center', fontFamily:font }}>
      <div style={{ backgroundColor:C.bgCard, borderRadius:'20px 20px 0 0', padding:'24px 20px 40px', width:'100%', maxWidth:'480px', maxHeight:'85vh', overflowY:'auto', position:'relative' }}>
        <div onClick={onClose} style={{ position:'absolute', top:'16px', right:'20px', fontSize:'24px', color:C.textMuted, cursor:'pointer' }}>✕</div>

        {sent ? (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            <div style={{ fontSize:'52px', marginBottom:'14px' }}>🎉</div>
            <div style={{ fontSize:'20px', fontWeight:'800', color:C.textPrimary, marginBottom:'8px' }}>Invite Sent!</div>
            <div style={{ fontSize:'14px', color:C.textMuted, marginBottom:'24px', lineHeight:'1.6' }}>
              Your friend will receive a Bracketz invite at <strong style={{ color:C.textPrimary }}>{email.trim()}</strong>
            </div>
            <div style={{ backgroundColor:C.orangeDim, border:`1px solid ${C.orangeBorder}`, borderRadius:'12px', padding:'14px', marginBottom:'24px' }}>
              <div style={{ fontSize:'12px', color:C.orange, fontWeight:'700' }}>🏆 You'll earn a referral badge when they join!</div>
            </div>
            <button
              onClick={() => { setSent(false); setEmail(''); setMessage(''); }}
              style={{ width:'100%', backgroundColor:C.orange, color:'#fff', border:'none', borderRadius:'12px', padding:'14px', fontSize:'14px', fontWeight:'700', cursor:'pointer', fontFamily:font, marginBottom:'10px' }}
            >
              Invite Another Friend
            </button>
            <button onClick={onClose} style={{ width:'100%', backgroundColor:'transparent', color:C.textMuted, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'12px', fontSize:'13px', cursor:'pointer', fontFamily:font }}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div style={{ fontSize:'18px', fontWeight:'800', color:C.textPrimary, marginBottom:'4px' }}>Invite a Friend</div>
            <div style={{ fontSize:'13px', color:C.textMuted, marginBottom:'20px' }}>Send them a Bracketz invite — earn a badge when they join!</div>

            {/* Referral badge teaser */}
            <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'12px 14px', marginBottom:'20px', display:'flex', gap:'10px', alignItems:'center' }}>
              <div style={{ display:'flex', gap:'6px' }}>
                {['🤝','👥','🌟','🚀'].map(icon => (
                  <span key={icon} style={{ fontSize:'18px' }}>{icon}</span>
                ))}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:'12px', fontWeight:'700', color:C.textPrimary }}>Earn Referral Badges</div>
                <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>1, 5, 10, and 25 accepted invites</div>
              </div>
            </div>

            <div style={{ marginBottom:'14px' }}>
              <div style={{ fontSize:'10px', fontWeight:'700', color:C.textMuted, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>Friend's Email</div>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                placeholder="friend@email.com"
                style={{ width:'100%', boxSizing:'border-box', backgroundColor:C.bgCard, border:`1px solid ${error ? '#EF4444' : C.border}`, borderRadius:'10px', padding:'12px 14px', color:C.textPrimary, fontSize:'14px', fontFamily:font, outline:'none' }}
              />
              {error && <div style={{ fontSize:'11px', color:'#EF4444', marginTop:'4px' }}>{error}</div>}
            </div>

            <div style={{ marginBottom:'20px' }}>
              <div style={{ fontSize:'10px', fontWeight:'700', color:C.textMuted, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>Personal Message <span style={{ fontWeight:'400', textTransform:'none', letterSpacing:'0' }}>(optional)</span></div>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={`Hey! I've been playing Bracketz every day — you'd love it.`}
                rows={3}
                maxLength={200}
                style={{ width:'100%', boxSizing:'border-box', backgroundColor:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'10px', padding:'12px 14px', color:C.textPrimary, fontSize:'13px', fontFamily:font, outline:'none', resize:'none', lineHeight:'1.5' }}
              />
              <div style={{ fontSize:'10px', color:C.textMuted, textAlign:'right', marginTop:'3px' }}>{message.length}/200</div>
            </div>

            <button
              onClick={handleSend}
              disabled={sending || !email.trim()}
              style={{ width:'100%', backgroundColor:sending||!email.trim()?'rgba(255,107,0,0.4)':C.orange, color:'#fff', border:'none', borderRadius:'12px', padding:'15px', fontSize:'15px', fontWeight:'700', cursor:sending||!email.trim()?'not-allowed':'pointer', fontFamily:font }}
            >
              {sending ? 'Sending...' : '📨 Send Invite'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── FIND FRIENDS MODAL ────────────────────────────────────────────────────────
function FindFriendsModal({ user, myUsername, friends, pendingSent, onClose, onRequestSent }) {
  const [search,  setSearch]  = useState('');
  const [results,     setResults]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [sending,     setSending]     = useState({});
  const [noResults,   setNoResults]   = useState(false);
  const [searchError, setSearchError] = useState('');

  const friendIds  = new Set(friends.map(f => f.userId));
  const pendingIds = new Set(pendingSent.map(p => p.toId));

  const handleSearch = async () => {
    const q = search.trim();
    if (!q || q.length < 2) return;
    setLoading(true);
    try {
      // Try exact match first
      const exactSnap = await getDocs(query(collection(db, 'userProfiles'), where('username', '==', q)));
      // Then try prefix range match
      const rangeSnap = await getDocs(query(collection(db, 'userProfiles'), where('username', '>=', q), where('username', '<=', q + '\uf8ff')));
      const all = [...exactSnap.docs, ...rangeSnap.docs];
      const seen = new Set();
      const results = [];
      for (const d of all) {
        if (d.id === user.uid || seen.has(d.id)) continue;
        seen.add(d.id);
        const data = d.data();
        if (data.username) results.push({ userId: d.id, ...data });
      }
      setResults(results);
      if (results.length === 0) setNoResults(true);
      else setNoResults(false);
    } catch(e) {
      console.error(e);
      setSearchError('Search failed. Your friend may need to set a username in their Profile first.');
    }
    setLoading(false);
  };

  const sendRequest = async (toId, toUsername) => {
    setSending(s => ({ ...s, [toId]: true }));
    try {
      await addDoc(collection(db, 'friendRequests'), { fromId:user.uid, fromUsername:myUsername, toId, toUsername, status:'pending', createdAt:serverTimestamp() });
      await addDoc(collection(db, 'notifications'), { userId:toId, type:'friend_request', title:`${myUsername} wants to be your friend!`, message:`Accept their request in the Friends tab.`, read:false, createdAt:serverTimestamp() });
      onRequestSent(toId);
    } catch(e) { console.error(e); }
    setSending(s => ({ ...s, [toId]: false }));
  };

  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.8)', zIndex:200, display:'flex', alignItems:'flex-end', justifyContent:'center', fontFamily:font }}>
      <div style={{ backgroundColor:C.bgCard, borderRadius:'20px 20px 0 0', padding:'24px 20px 40px', width:'100%', maxWidth:'480px', maxHeight:'85vh', overflowY:'auto', position:'relative' }}>
        <div onClick={onClose} style={{ position:'absolute', top:'16px', right:'20px', fontSize:'24px', color:C.textMuted, cursor:'pointer' }}>✕</div>
        <div style={{ fontSize:'18px', fontWeight:'800', color:C.textPrimary, marginBottom:'16px' }}>Find Friends</div>
        <div style={{ display:'flex', gap:'8px', marginBottom:'16px' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key==='Enter'&&handleSearch()} placeholder="Search by username..." style={{ flex:1, backgroundColor:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'10px', padding:'10px 14px', color:C.textPrimary, fontSize:'16px', fontFamily:font, outline:'none' }}/>
          <button onClick={handleSearch} style={{ backgroundColor:C.orange, color:'#fff', border:'none', borderRadius:'10px', padding:'10px 16px', fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:font }}>Search</button>
        </div>
        {loading && <div style={{ textAlign:'center', color:C.textMuted, fontSize:'13px', padding:'20px' }}>Searching...</div>}
        {results.length > 0 && (
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {results.map(profile => {
              const isFriend  = friendIds.has(profile.userId);
              const isPending = pendingIds.has(profile.userId);
              return (
                <div key={profile.userId} style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px' }}>
                  <Avatar username={profile.username}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary }}>{profile.username}{profile.isPro&&<ProBadge/>}</div>
                    <div style={{ fontSize:'11px', color:C.textMuted, marginTop:'2px' }}>{profile.streakCount||0} day streak</div>
                  </div>
                  {isFriend ? <div style={{ fontSize:'11px', color:C.green, fontWeight:'700' }}>✔ Friends</div>
                  : isPending ? <div style={{ fontSize:'11px', color:C.textMuted, fontWeight:'700' }}>Pending</div>
                  : <button onClick={() => sendRequest(profile.userId, profile.username)} disabled={sending[profile.userId]} style={{ backgroundColor:sending[profile.userId]?'rgba(255,107,0,0.4)':C.orange, color:'#fff', border:'none', borderRadius:'8px', padding:'7px 14px', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:font }}>{sending[profile.userId]?'...':'Add'}</button>}
                </div>
              );
            })}
          </div>
        )}
        {searchError && <div style={{ fontSize:'12px', color:'#EF4444', padding:'12px', textAlign:'center' }}>{searchError}</div>}
        {!loading && noResults && results.length===0 && !searchError && <div style={{ textAlign:'center', padding:'20px' }}><div style={{ color:C.textMuted, fontSize:'13px', marginBottom:'6px' }}>No users found for "{search}"</div><div style={{ color:C.textMuted, fontSize:'11px' }}>Make sure your friend has set a username in their Profile tab.</div></div>}
        {results.length===0 && search.trim().length===0 && <div style={{ textAlign:'center', color:C.textMuted, fontSize:'13px', padding:'20px' }}>Type a username and tap Search to find friends.</div>}
      </div>
    </div>
  );
}

// ── FRIEND CHAT ───────────────────────────────────────────────────────────────
function FriendChat({ user, myUsername, friend, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text,     setText]     = useState('');
  const [sending,  setSending]  = useState(false);
  const bottomRef = useRef(null);
  const fid       = friendshipId(user.uid, friend.userId);

  useEffect(() => {
    const q = query(collection(db, 'friendMessages', fid, 'messages'), orderBy('createdAt', 'asc'), limit(100));
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior:'smooth' }), 50);
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fid]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'friendMessages', fid, 'messages'), { senderId:user.uid, username:myUsername, text:text.trim(), createdAt:serverTimestamp() });
      setText('');
    } catch(e) { console.error(e); }
    setSending(false);
  };

  const timeAgo = (ts) => {
    if (!ts) return '';
    try {
      const diff = Math.floor((new Date() - ts.toDate()) / 60000);
      if (diff < 1) return 'just now';
      if (diff < 60) return `${diff}m ago`;
      if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
      return `${Math.floor(diff/1440)}d ago`;
    } catch { return ''; }
  };

  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:C.bg, zIndex:300, display:'flex', flexDirection:'column', fontFamily:font, maxWidth:'480px', margin:'0 auto' }}>
      <div style={{ backgroundColor:C.bgCard, padding:'14px 18px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:'12px' }}>
        <div onClick={onClose} style={{ fontSize:'22px', color:C.textMuted, cursor:'pointer', marginRight:'4px' }}>‹</div>
        <Avatar username={friend.username} orange/>
        <div>
          <div style={{ fontSize:'15px', fontWeight:'800', color:C.textPrimary }}>{friend.username}{friend.isPro&&<ProBadge/>}</div>
          <div style={{ fontSize:'11px', color:C.textMuted }}>{friend.streak||0} day streak 🔥</div>
        </div>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'16px', display:'flex', flexDirection:'column', gap:'8px' }}>
        {messages.length===0 && <div style={{ textAlign:'center', color:C.textMuted, fontSize:'13px', marginTop:'40px' }}>No messages yet. Say something!</div>}
        {messages.map(msg => {
          const isMe = msg.senderId === user.uid;
          return (
            <div key={msg.id} style={{ display:'flex', flexDirection:'column', alignItems:isMe?'flex-end':'flex-start' }}>
              <div style={{ backgroundColor:isMe?C.orange:C.bgCard, border:`1px solid ${isMe?'transparent':C.border}`, borderRadius:isMe?'14px 14px 4px 14px':'14px 14px 14px 4px', padding:'10px 14px', maxWidth:'75%' }}>
                <div style={{ fontSize:'14px', color:isMe?'#fff':C.textPrimary, lineHeight:'1.4' }}>{msg.text}</div>
              </div>
              <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'3px' }}>{timeAgo(msg.createdAt)}</div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>
      <div style={{ backgroundColor:C.bgCard, borderTop:`1px solid ${C.border}`, padding:'12px 16px', display:'flex', gap:'8px', alignItems:'center' }}>
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key==='Enter'&&!e.shiftKey&&send()} placeholder="Message..." style={{ flex:1, backgroundColor:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'20px', padding:'10px 16px', color:C.textPrimary, fontSize:'14px', fontFamily:font, outline:'none' }}/>
        <button onClick={send} disabled={sending||!text.trim()} style={{ backgroundColor:text.trim()?C.orange:'rgba(255,107,0,0.3)', color:'#fff', border:'none', borderRadius:'50%', width:'38px', height:'38px', fontSize:'16px', cursor:text.trim()?'pointer':'default', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>›</button>
      </div>
    </div>
  );
}

// ── PENDING REQUESTS ──────────────────────────────────────────────────────────
function PendingRequests({ user, myUsername, requests, onAccept, onDecline }) {
  if (requests.length === 0) return null;
  return (
    <div style={{ margin:'0 0 16px' }}>
      <div style={{ fontSize:'9px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'8px', paddingBottom:'6px', borderBottom:`1px solid ${C.border}` }}>Friend Requests</div>
      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
        {requests.map(req => (
          <div key={req.id} style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.orangeBorder}`, padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px' }}>
            <Avatar username={req.fromUsername} orange/>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary }}>{req.fromUsername}</div>
              <div style={{ fontSize:'11px', color:C.textMuted, marginTop:'2px' }}>wants to be your friend</div>
            </div>
            <div style={{ display:'flex', gap:'6px' }}>
              <button onClick={() => onDecline(req)} style={{ backgroundColor:'transparent', color:C.textMuted, border:`1px solid ${C.border}`, borderRadius:'8px', padding:'6px 12px', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:font }}>✕</button>
              <button onClick={() => onAccept(req)} style={{ backgroundColor:C.green, color:'#fff', border:'none', borderRadius:'8px', padding:'6px 12px', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:font }}>✔ Accept</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MAIN FRIENDS COMPONENT ────────────────────────────────────────────────────

// ── PRO BADGE ─────────────────────────────────────────────────────────────────
function ProBadge() {
  return (
    <span style={{ fontSize:'11px', marginLeft:'4px', verticalAlign:'middle' }} title="Bracketz Pro">⚡</span>
  );
}

// ── CREATE GROUP MODAL ────────────────────────────────────────────────────────
function CreateGroupModal({ user, myUsername, friends, friendStats, onClose, onCreated }) {
  const [name,     setName]     = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const toggle = (uid) => {
    setSelected(s => {
      const n = new Set(s);
      n.has(uid) ? n.delete(uid) : n.add(uid);
      return n;
    });
  };

  const handleCreate = async () => {
    if (!name.trim()) { setError('Please enter a group name'); return; }
    setSaving(true);
    try {
      const members = [user.uid, ...Array.from(selected)];
      const memberUsernames = { [user.uid]: myUsername };
      friends.filter(f => selected.has(f.userId)).forEach(f => {
        memberUsernames[f.userId] = friendStats[f.userId]?.username || f.username;
      });
      const ref = await addDoc(collection(db, 'groups'), {
        name: name.trim(),
        createdBy: user.uid,
        members,
        memberUsernames,
        createdAt: serverTimestamp(),
      });
      
      // Notify each added friend
      const addedFriends = friends.filter(f => selected.has(f.userId));
      for (const friend of addedFriends) {
        await addDoc(collection(db, 'notifications'), {
          userId: friend.userId,
          type: 'group_added',
          title: `${myUsername} added you to a group!`,
          message: `You've been added to "${name.trim()}". Check it out in the Friends tab.`,
          read: false,
          createdAt: serverTimestamp(),
        });
        // Email — queue for Mailgun once DKIM verified
        await addDoc(collection(db, 'emailQueue'), {
          to: friendStats[friend.userId]?.email || null,
          toUserId: friend.userId,
          subject: `${myUsername} added you to a Bracketz group!`,
          body: `You've been added to the group "${name.trim()}" on Bracketz. Open the app and check the Friends tab to see your group picks and chat.`,
          type: 'group_added',
          status: 'pending',
          createdAt: serverTimestamp(),
        });
      }
      
      onCreated(ref.id);
    } catch(e) { console.error(e); setError('Failed to create group. Try again.'); }
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.8)', zIndex:200, display:'flex', alignItems:'flex-end', justifyContent:'center', fontFamily:font }}>
      <div style={{ backgroundColor:C.bgCard, borderRadius:'20px 20px 0 0', padding:'24px 20px 40px', width:'100%', maxWidth:'480px', maxHeight:'85vh', overflowY:'auto', position:'relative' }}>
        <div onClick={onClose} style={{ position:'absolute', top:'16px', right:'20px', fontSize:'24px', color:C.textMuted, cursor:'pointer' }}>✕</div>
        <div style={{ fontSize:'18px', fontWeight:'800', color:C.textPrimary, marginBottom:'4px' }}>Create a Group</div>
        <div style={{ fontSize:'13px', color:C.textMuted, marginBottom:'20px' }}>Compare picks and chat privately with your group.</div>

        <div style={{ marginBottom:'16px' }}>
          <div style={{ fontSize:'10px', fontWeight:'700', color:C.textMuted, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>Group Name</div>
          <input
            value={name}
            onChange={e => { setName(e.target.value); setError(''); }}
            placeholder="e.g. The Sports Crew"
            maxLength={40}
            style={{ width:'100%', boxSizing:'border-box', backgroundColor:C.bgCard, border:`1px solid ${error?'#EF4444':C.border}`, borderRadius:'10px', padding:'12px 14px', color:C.textPrimary, fontSize:'14px', fontFamily:font, outline:'none' }}
          />
          {error && <div style={{ fontSize:'11px', color:'#EF4444', marginTop:'4px' }}>{error}</div>}
        </div>

        <div style={{ fontSize:'10px', fontWeight:'700', color:C.textMuted, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'8px' }}>Add Friends ({selected.size} selected)</div>
        {friends.length === 0 ? (
          <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'16px', textAlign:'center' }}>
            <div style={{ fontSize:'13px', color:C.textMuted, marginBottom:'4px' }}>You need at least one friend to create a group.</div>
            <div style={{ fontSize:'11px', color:C.textMuted }}>Find friends using the 🔍 Find button on the Friends tab.</div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'20px' }}>
            {friends.map(friend => {
              const stats = friendStats[friend.userId] || {};
              const checked = selected.has(friend.userId);
              return (
                <div key={friend.userId} onClick={() => toggle(friend.userId)}
                  style={{ backgroundColor:checked?C.bgCard:C.bgCard, borderRadius:'12px', border:`1px solid ${checked?C.orange:C.border}`, padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer', transition:'all 0.15s' }}>
                  <Avatar username={stats.username||friend.username} orange={checked}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary }}>{stats.username||friend.username}</div>
                    <div style={{ fontSize:'11px', color:C.textMuted }}>{stats.streak||0} day streak</div>
                  </div>
                  <div style={{ width:'22px', height:'22px', borderRadius:'50%', backgroundColor:checked?C.orange:'transparent', border:`1.5px solid ${checked?C.orange:C.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {checked && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button onClick={handleCreate} disabled={saving || !name.trim() || (friends.length === 0)}
          style={{ width:'100%', backgroundColor:saving||!name.trim()?'rgba(255,107,0,0.4)':C.orange, color:'#fff', border:'none', borderRadius:'12px', padding:'15px', fontSize:'15px', fontWeight:'700', cursor:saving||!name.trim()?'not-allowed':'pointer', fontFamily:font }}>
          {saving ? 'Creating...' : '👥 Create Group'}
        </button>
      </div>
    </div>
  );
}

// ── GROUP DETAIL ──────────────────────────────────────────────────────────────
function GroupDetail({ user, myUsername, group, onBack }) {
  const [messages,   setMessages]   = useState([]);
  const [text,       setText]       = useState('');
  const [sending,    setSending]    = useState(false);
  const [picks,      setPicks]      = useState({});  // { userId: { champion, submitted } }
  const [tab,        setTab]        = useState('picks'); // 'picks' | 'chat'
  const [todayBracket, setTodayBracket] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    // Load today's bracket
    const loadBracket = async () => {
      try {
        const today = getLocalDateString();
        const snap = await getDocs(query(collection(db, 'brackets'), where('date', '==', today)));
        if (!snap.empty) setTodayBracket({ id: snap.docs[0].id, ...snap.docs[0].data() });
      } catch(e) {}
    };
    loadBracket();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!todayBracket) return;
    // Load each member's picks
    const loadPicks = async () => {
      const result = {};
      for (const uid of group.members) {
        try {
          const snap = await getDocs(query(collection(db, 'userBrackets'), where('userId', '==', uid), where('bracketId', '==', todayBracket.id), where('isSubmitted', '==', true)));
          if (!snap.empty) {
            result[uid] = { submitted: true, champion: snap.docs[0].data().picks?.['r4_m0'] || null };
          } else {
            result[uid] = { submitted: false, champion: null };
          }
        } catch(e) { result[uid] = { submitted: false, champion: null }; }
      }
      setPicks(result);
    };
    loadPicks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayBracket]);

  useEffect(() => {
    // Real-time group chat
    const q = query(collection(db, 'groups', group.id, 'messages'), orderBy('createdAt', 'asc'), limit(100));
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  const sendMessage = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'groups', group.id, 'messages'), {
        senderId: user.uid,
        username: myUsername,
        text: text.trim(),
        createdAt: serverTimestamp(),
      });
      setText('');
    } catch(e) { console.error(e); }
    setSending(false);
  };

  const timeAgo = (ts) => {
    if (!ts) return '';
    try {
      const diff = Math.floor((new Date() - ts.toDate()) / 60000);
      if (diff < 1) return 'just now';
      if (diff < 60) return `${diff}m ago`;
      if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
      return `${Math.floor(diff/1440)}d ago`;
    } catch { return ''; }
  };

  const myPick = picks[user.uid];
  const memberCount = group.members.length;
  const submittedCount = Object.values(picks).filter(p => p.submitted).length;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', fontFamily:font }}>
      {/* Header */}
      <div style={{ padding:'14px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:'12px' }}>
        <div onClick={onBack} style={{ fontSize:'22px', color:C.textMuted, cursor:'pointer' }}>‹</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:'16px', fontWeight:'800', color:C.textPrimary }}>{group.name}</div>
          <div style={{ fontSize:'11px', color:C.textMuted }}>{memberCount} member{memberCount!==1?'s':''} · {submittedCount} submitted today</div>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display:'flex', backgroundColor:C.bgCard, margin:'12px 16px 0', borderRadius:'8px', padding:'3px', border:`1px solid ${C.border}` }}>
        {[{ key:"picks", label:"📊 Today's Picks" }, { key:"chat", label:"💬 Group Chat" }].map(t => (
          <div key={t.key} onClick={() => setTab(t.key)}
            style={{ flex:1, padding:'8px', textAlign:'center', fontSize:'11px', fontWeight:'700', borderRadius:'6px', cursor:'pointer', backgroundColor:tab===t.key?C.orange:'transparent', color:tab===t.key?'#FFF':C.textMuted, transition:'all 0.15s' }}>
            {t.label}
          </div>
        ))}
      </div>

      {/* Picks tab */}
      {tab === 'picks' && (
        <div style={{ flex:1, overflowY:'auto', padding:'12px 16px' }}>
          {todayBracket && (
            <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'12px 14px', marginBottom:'12px' }}>
              <div style={{ fontSize:'9px', color:C.orange, fontWeight:'700', letterSpacing:'2px', textTransform:'uppercase', marginBottom:'4px' }}>Today's Bracket</div>
              <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary }}>{todayBracket.category}</div>
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {group.members.map(uid => {
              const username = group.memberUsernames?.[uid] || uid;
              const isMe = uid === user.uid;
              const pick = picks[uid];
              const bothSubmitted = myPick?.submitted && pick?.submitted;
              return (
                <div key={uid} style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${isMe?C.orangeBorder:C.border}`, padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px' }}>
                  <Avatar username={username} orange={isMe}/>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                      <span style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary }}>{username}</span>
                      {isMe && <span style={{ fontSize:'9px', backgroundColor:C.orangeDim, color:C.orange, padding:'2px 6px', borderRadius:'6px', fontWeight:'700' }}>YOU</span>}
                    </div>
                    {pick?.submitted ? (
                      <div style={{ fontSize:'11px', color:C.green, marginTop:'2px' }}>✔ Submitted</div>
                    ) : (
                      <div style={{ fontSize:'11px', color:C.textMuted, marginTop:'2px' }}>Not played yet</div>
                    )}
                  </div>
                  {pick?.submitted && bothSubmitted && pick?.champion ? (
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:'9px', color:C.textMuted, marginBottom:'2px' }}>Champion</div>
                      <div style={{ fontSize:'12px', fontWeight:'700', color:C.orange }}>{pick.champion}</div>
                    </div>
                  ) : pick?.submitted && !myPick?.submitted ? (
                    <div style={{ backgroundColor:C.bgCard, borderRadius:'8px', padding:'4px 10px' }}>
                      <div style={{ fontSize:'10px', color:C.textMuted }}>Submit to reveal</div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Chat tab */}
      {tab === 'chat' && (
        <>
          <div style={{ flex:1, overflowY:'auto', padding:'12px 16px', display:'flex', flexDirection:'column', gap:'8px' }}>
            {messages.length === 0 && (
              <div style={{ textAlign:'center', color:C.textMuted, fontSize:'13px', marginTop:'40px' }}>No messages yet. Start the conversation!</div>
            )}
            {messages.map(msg => {
              const isMe = msg.senderId === user.uid;
              return (
                <div key={msg.id} style={{ display:'flex', flexDirection:'column', alignItems:isMe?'flex-end':'flex-start' }}>
                  {!isMe && <div style={{ fontSize:'10px', color:C.textMuted, marginBottom:'3px', marginLeft:'4px' }}>{msg.username}</div>}
                  <div style={{ backgroundColor:isMe?C.orange:C.bgCard, border:`1px solid ${isMe?'transparent':C.border}`, borderRadius:isMe?'14px 14px 4px 14px':'14px 14px 14px 4px', padding:'10px 14px', maxWidth:'78%' }}>
                    <div style={{ fontSize:'14px', color:isMe?'#fff':C.textPrimary, lineHeight:'1.4' }}>{msg.text}</div>
                  </div>
                  <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'3px' }}>{timeAgo(msg.createdAt)}</div>
                </div>
              );
            })}
            <div ref={bottomRef}/>
          </div>
          <div style={{ backgroundColor:C.bgCard, borderTop:`1px solid ${C.border}`, padding:'12px 16px', display:'flex', gap:'8px', alignItems:'center' }}>
            <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key==='Enter'&&!e.shiftKey&&sendMessage()}
              placeholder="Message the group..."
              style={{ flex:1, backgroundColor:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'20px', padding:'10px 16px', color:C.textPrimary, fontSize:'14px', fontFamily:font, outline:'none' }}/>
            <button onClick={sendMessage} disabled={sending||!text.trim()}
              style={{ backgroundColor:text.trim()?C.orange:'rgba(255,107,0,0.3)', color:'#fff', border:'none', borderRadius:'50%', width:'38px', height:'38px', fontSize:'16px', cursor:text.trim()?'pointer':'default', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>›</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── GROUPS SECTION ────────────────────────────────────────────────────────────
function GroupsSection({ user, myUsername, friends, friendStats, onOpenGroup }) {
  const [groups,      setGroups]      = useState([]);
  const [showCreate,  setShowCreate]  = useState(false);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => { loadGroups(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGroups = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'groups'), where('members', 'array-contains', user.uid)));
      setGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  return (
    <>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px', paddingBottom:'6px', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ fontSize:'9px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase' }}>Groups</div>
        <button onClick={() => setShowCreate(true)}
          style={{ backgroundColor:'transparent', color:C.orange, border:`1px solid ${C.orangeBorder}`, borderRadius:'20px', padding:'4px 12px', fontSize:'11px', fontWeight:'700', cursor:'pointer', fontFamily:font }}>
          + New Group
        </button>
      </div>

      {loading ? (
        <div style={{ color:C.textMuted, fontSize:'12px', padding:'12px 0' }}>Loading groups...</div>
      ) : groups.length === 0 ? (
        <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'20px', textAlign:'center', marginBottom:'16px' }}>
          <div style={{ fontSize:'28px', marginBottom:'8px' }}>👥</div>
          <div style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary, marginBottom:'4px' }}>No groups yet</div>
          <div style={{ fontSize:'11px', color:C.textMuted, marginBottom:'12px' }}>Create a group to compare picks and chat privately with friends.</div>
          <button onClick={() => setShowCreate(true)}
            style={{ backgroundColor:C.orange, color:'#fff', border:'none', borderRadius:'10px', padding:'10px 18px', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:font }}>
            Create Your First Group
          </button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'20px' }}>
          {groups.map(group => (
            <div key={group.id} onClick={() => onOpenGroup(group)}
              style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}>
              <div style={{ width:'38px', height:'38px', borderRadius:'10px', backgroundColor:C.orangeDim, border:`1px solid ${C.orangeBorder}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px', flexShrink:0 }}>👥</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary }}>{group.name}</div>
                <div style={{ fontSize:'11px', color:C.textMuted, marginTop:'2px' }}>{group.members.length} member{group.members.length!==1?'s':''}</div>
              </div>
              <div style={{ color:C.textMuted, fontSize:'18px' }}>›</div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateGroupModal
          user={user}
          myUsername={myUsername}
          friends={friends}
          friendStats={friendStats}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); loadGroups(); }}
        />
      )}
    </>
  );
}

function Friends({ user }) {
  const [myUsername,      setMyUsername]      = useState('');
  const [myIsPro,        setMyIsPro]        = useState(false);
  const [friends,         setFriends]         = useState([]);
  const [pendingReceived, setPendingReceived] = useState([]);
  const [pendingSent,     setPendingSent]     = useState([]);
  const [friendStats,     setFriendStats]     = useState({});
  const [todayActivity,   setTodayActivity]   = useState({});
  const [loading,         setLoading]         = useState(true);
  const [showFind,        setShowFind]        = useState(false);
  const [showInvite,      setShowInvite]      = useState(false);
  const [activeFriend,    setActiveFriend]    = useState(null);
  const [lbTab,           setLbTab]           = useState('streak');
  const [localPending,    setLocalPending]    = useState(new Set());
  const [mySubmitted,     setMySubmitted]     = useState(false);
  const [activeGroup,     setActiveGroup]     = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const checkMySubmission = async () => {
      try {
        const today       = getLocalDateString();
        const bracketSnap = await getDocs(query(collection(db, 'brackets'), where('date', '==', today)));
        if (bracketSnap.empty) return;
        const todayId = bracketSnap.docs[0].id;
        const ubSnap  = await getDocs(query(collection(db, 'userBrackets'), where('userId', '==', user.uid), where('bracketId', '==', todayId), where('isSubmitted', '==', true)));
        setMySubmitted(!ubSnap.empty);
      } catch(e) {}
    };
    checkMySubmission();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const profileSnap = await getDoc(doc(db, 'userProfiles', user.uid));
      const profileData = profileSnap.exists() ? profileSnap.data() : {};
      const username = profileData.username || user.email.split('@')[0];
      setMyUsername(username);
      setMyIsPro(profileData.isPro || false);

      const receivedSnap = await getDocs(query(collection(db, 'friendRequests'), where('toId', '==', user.uid), where('status', '==', 'pending')));
      setPendingReceived(receivedSnap.docs.map(d => ({ id:d.id, ...d.data() })));

      const sentSnap = await getDocs(query(collection(db, 'friendRequests'), where('fromId', '==', user.uid), where('status', '==', 'pending')));
      setPendingSent(sentSnap.docs.map(d => ({ id:d.id, ...d.data() })));

      const [acceptedFrom, acceptedTo] = await Promise.all([
        getDocs(query(collection(db, 'friendRequests'), where('fromId', '==', user.uid), where('status', '==', 'accepted'))),
        getDocs(query(collection(db, 'friendRequests'), where('toId',   '==', user.uid), where('status', '==', 'accepted'))),
      ]);
      const friendList = [
        ...acceptedFrom.docs.map(d => ({ reqId:d.id, userId:d.data().toId,   username:d.data().toUsername   })),
        ...acceptedTo.docs.map(d   => ({ reqId:d.id, userId:d.data().fromId, username:d.data().fromUsername })),
      ];
      setFriends(friendList);
      if (friendList.length > 0) {
        await loadFriendStats(friendList);
        await loadTodayActivity(friendList);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const loadFriendStats = async (friendList) => {
    try {
      const stats = {};
      for (const friend of friendList) {
        const profileSnap = await getDoc(doc(db, 'userProfiles', friend.userId));
        if (profileSnap.exists()) {
          const data = profileSnap.data();
          stats[friend.userId] = { streak:data.streakCount||0, longestStreak:data.longestStreak||0, username:data.username||friend.username, isPro:data.isPro||false };
        } else {
          stats[friend.userId] = { streak:0, longestStreak:0, username:friend.username };
        }
        const ubSnap = await getDocs(query(collection(db, 'userBrackets'), where('userId', '==', friend.userId), where('isSubmitted', '==', true)));
        stats[friend.userId].total = ubSnap.size;
      }
      setFriendStats(stats);
    } catch(e) { console.error(e); }
  };

  const loadTodayActivity = async (friendList) => {
    try {
      const today = getLocalDateString();
      const bracketSnap = await getDocs(query(collection(db, 'brackets'), where('date', '==', today)));
      if (bracketSnap.empty) return;
      const todayBracketId = bracketSnap.docs[0].id;
      const activity = {};
      for (const friend of friendList) {
        const ubSnap = await getDocs(query(collection(db, 'userBrackets'), where('userId', '==', friend.userId), where('bracketId', '==', todayBracketId), where('isSubmitted', '==', true)));
        activity[friend.userId] = ubSnap.empty ? { submitted:false, champion:null } : { submitted:true, champion:ubSnap.docs[0].data().picks?.['r4_m0']||null };
      }
      setTodayActivity(activity);
    } catch(e) { console.error(e); }
  };

  const acceptRequest = async (req) => {
    try {
      await updateDoc(doc(db, 'friendRequests', req.id), { status:'accepted' });
      await addDoc(collection(db, 'notifications'), { userId:req.fromId, type:'friend_accepted', title:`${myUsername} accepted your friend request!`, message:`You and ${myUsername} are now friends on Bracketz.`, read:false, createdAt:serverTimestamp() });
      setPendingReceived(p => p.filter(r => r.id !== req.id));
      await loadAll();
    } catch(e) { console.error(e); }
  };

  const declineRequest = async (req) => {
    try {
      await updateDoc(doc(db, 'friendRequests', req.id), { status:'declined' });
      setPendingReceived(p => p.filter(r => r.id !== req.id));
    } catch(e) { console.error(e); }
  };

  const unfriend = async (friend) => {
    try {
      await deleteDoc(doc(db, 'friendRequests', friend.reqId));
      setFriends(f => f.filter(fr => fr.userId !== friend.userId));
    } catch(e) { console.error(e); }
  };

  const sortedFriends = [...friends].sort((a, b) => {
    const sa = friendStats[a.userId] || {}, sb = friendStats[b.userId] || {};
    return lbTab === 'streak' ? (sb.streak||0) - (sa.streak||0) : (sb.total||0) - (sa.total||0);
  });

  if (loading) return <div style={{ padding:'16px', fontFamily:font }}><div style={{ textAlign:'center', padding:'60px', color:C.textMuted, fontSize:'13px' }}>Loading friends...</div></div>;

  if (activeGroup) return <GroupDetail user={user} myUsername={myUsername} group={activeGroup} onBack={() => setActiveGroup(null)}/>;
  if (activeFriend) return <FriendChat user={user} myUsername={myUsername} friend={{ ...activeFriend, ...(friendStats[activeFriend.userId]||{}) }} onClose={() => setActiveFriend(null)}/>;

  return (
    <div style={{ padding:'16px', fontFamily:font }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
        <div>
          <div style={{ fontSize:'22px', fontWeight:'800', color:C.textPrimary }}>Friends</div>
          <div style={{ fontSize:'12px', color:C.textMuted, marginTop:'3px' }}>{friends.length} friend{friends.length!==1?'s':''}</div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={() => setShowInvite(true)} style={{ backgroundColor:'transparent', color:C.orange, border:`1px solid ${C.orange}`, borderRadius:'20px', padding:'8px 14px', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:font }}>
            📨 Invite
          </button>
          <button onClick={() => setShowFind(true)} style={{ backgroundColor:C.orange, color:'#fff', border:'none', borderRadius:'20px', padding:'8px 14px', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:font, display:'flex', alignItems:'center', gap:'6px' }}>
            🔍 Find
            {pendingReceived.length > 0 && <span style={{ backgroundColor:'#fff', color:C.orange, borderRadius:'50%', width:'16px', height:'16px', fontSize:'9px', fontWeight:'800', display:'flex', alignItems:'center', justifyContent:'center' }}>{pendingReceived.length}</span>}
          </button>
        </div>
      </div>

      <PendingRequests user={user} myUsername={myUsername} requests={pendingReceived} onAccept={acceptRequest} onDecline={declineRequest}/>

      <GroupsSection user={user} myUsername={myUsername} friends={friends} friendStats={friendStats} onOpenGroup={setActiveGroup}/>

      {/* Empty state */}
      {friends.length === 0 && pendingReceived.length === 0 && (
        <div style={{ backgroundColor:C.bgCard, borderRadius:'16px', border:`1px solid ${C.border}`, padding:'40px 24px', textAlign:'center', marginBottom:'16px' }}>
          <div style={{ fontSize:'48px', marginBottom:'14px' }}>👥</div>
          <div style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, marginBottom:'8px' }}>No friends yet</div>
          <div style={{ fontSize:'13px', color:C.textMuted, marginBottom:'20px' }}>Find friends by username or invite someone new to Bracketz.</div>
          <div style={{ display:'flex', gap:'10px', justifyContent:'center' }}>
            <button onClick={() => setShowFind(true)} style={{ backgroundColor:C.orange, color:'#fff', border:'none', borderRadius:'12px', padding:'12px 20px', fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:font }}>🔍 Find Friends</button>
            <button onClick={() => setShowInvite(true)} style={{ backgroundColor:'transparent', color:C.orange, border:`1px solid ${C.orange}`, borderRadius:'12px', padding:'12px 20px', fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:font }}>📨 Invite</button>
          </div>
        </div>
      )}

      {friends.length > 0 && (
        <>
          <div style={{ fontSize:'9px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'8px', paddingBottom:'6px', borderBottom:`1px solid ${C.border}` }}>Leaderboard</div>
          <div style={{ display:'flex', backgroundColor:C.bgCard, borderRadius:'8px', padding:'3px', marginBottom:'12px', border:`1px solid ${C.border}` }}>
            {[{ key:'streak', label:'🔥 Streak' },{ key:'total', label:'🏆 Brackets' }].map(t => (
              <div key={t.key} onClick={() => setLbTab(t.key)} style={{ flex:1, padding:'7px', textAlign:'center', fontSize:'11px', fontWeight:'700', borderRadius:'6px', cursor:'pointer', backgroundColor:lbTab===t.key?C.orange:'transparent', color:lbTab===t.key?'#FFF':C.textMuted, transition:'all 0.15s' }}>{t.label}</div>
            ))}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'20px' }}>
            {sortedFriends.map((friend, idx) => {
              const stats = friendStats[friend.userId]||{};
              const medals = ['🥇','🥈','🥉'];
              const value = lbTab==='streak' ? `${stats.streak||0} 🔥` : `${stats.total||0}`;
              return (
                <div key={friend.userId} onClick={() => setActiveFriend(friend)} style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}>
                  <div style={{ width:'28px', textAlign:'center', flexShrink:0 }}>
                    {medals[idx] ? <span style={{ fontSize:'18px' }}>{medals[idx]}</span> : <span style={{ fontSize:'13px', fontWeight:'700', color:C.textMuted }}>#{idx+1}</span>}
                  </div>
                  <Avatar username={stats.username||friend.username}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary }}>{stats.username||friend.username}{stats.isPro&&<ProBadge/>}</div>
                    <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'2px' }}>{stats.total||0} brackets submitted</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:'16px', fontWeight:'800', color:C.orange }}>{value}</div>
                    <div style={{ fontSize:'9px', color:C.textMuted }}>{lbTab==='streak'?'day streak':'brackets'}</div>
                  </div>
                  <div style={{ color:C.textMuted, fontSize:'18px' }}>›</div>
                </div>
              );
            })}
          </div>

          <div style={{ fontSize:'9px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'8px', paddingBottom:'6px', borderBottom:`1px solid ${C.border}` }}>Today's Activity</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'20px' }}>
            {friends.map(friend => {
              const activity = todayActivity[friend.userId];
              const stats    = friendStats[friend.userId]||{};
              const bothDone = mySubmitted && activity?.submitted;
              return (
                <div key={friend.userId} style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px' }}>
                  <Avatar username={stats.username||friend.username}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary }}>{stats.username||friend.username}{stats.isPro&&<ProBadge/>}</div>
                    {activity?.submitted ? <div style={{ fontSize:'11px', color:C.green, marginTop:'2px' }}>✔ Submitted today</div> : <div style={{ fontSize:'11px', color:C.textMuted, marginTop:'2px' }}>Not played yet</div>}
                  </div>
                  {bothDone && activity?.champion ? (
                    <div style={{ textAlign:'right' }}><div style={{ fontSize:'9px', color:C.textMuted, marginBottom:'2px' }}>Champion</div><div style={{ fontSize:'11px', fontWeight:'700', color:C.orange }}>{activity.champion}</div></div>
                  ) : activity?.submitted && !mySubmitted ? (
                    <div style={{ backgroundColor:C.bgCard, borderRadius:'8px', padding:'4px 10px' }}><div style={{ fontSize:'10px', color:C.textMuted }}>Submit to reveal</div></div>
                  ) : null}
                  <button onClick={() => setActiveFriend(friend)} style={{ backgroundColor:'transparent', color:C.orange, border:`1px solid ${C.orangeBorder}`, borderRadius:'8px', padding:'6px 12px', fontSize:'11px', fontWeight:'700', cursor:'pointer', fontFamily:font, flexShrink:0 }}>💬</button>
                </div>
              );
            })}
          </div>

          <div style={{ fontSize:'9px', fontWeight:'700', color:C.textMuted, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'8px', paddingBottom:'6px', borderBottom:`1px solid ${C.border}` }}>Manage Friends</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'16px' }}>
            {friends.map(friend => {
              const stats = friendStats[friend.userId]||{};
              return (
                <div key={friend.userId} style={{ backgroundColor:C.bgCard, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'10px 14px', display:'flex', alignItems:'center', gap:'12px' }}>
                  <Avatar username={stats.username||friend.username} size={28}/>
                  <div style={{ flex:1, fontSize:'13px', color:C.textSecondary }}>{stats.username||friend.username}{stats.isPro&&<ProBadge/>}</div>
                  <button onClick={() => unfriend(friend)} style={{ backgroundColor:'transparent', color:C.textMuted, border:`1px solid ${C.border}`, borderRadius:'8px', padding:'5px 10px', fontSize:'11px', cursor:'pointer', fontFamily:font }}>Unfriend</button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {showFind && <FindFriendsModal user={user} myUsername={myUsername} friends={friends} pendingSent={[...pendingSent, ...[...localPending].map(id => ({ toId:id }))]} onClose={() => setShowFind(false)} onRequestSent={id => setLocalPending(s => new Set([...s, id]))}/>}
      {showInvite && <InviteFriendsModal user={user} myUsername={myUsername} onClose={() => setShowInvite(false)}/>}

      <div style={{ height:'20px' }}/>
    </div>
  );
}

export default Friends;
